import { ServerErrorResponse } from '../utils/errors';
import log from 'loglevel';

export type ApiTokenResponse = {
    token: string,
    isSso : boolean
}

export type ApiTokenProviderCallback = () => Promise<ApiTokenResponse>;

let apiTokenProviderCallback: ApiTokenProviderCallback | null = null;

// Method registers the provided callback which is invoked for every request for which a token is required.
export function RegisterApiTokenProviderCallback(callback: ApiTokenProviderCallback) : void {
    apiTokenProviderCallback = callback;
}

// Method clears the callback which is invoked for every request for which a token is required.
export function ClearApiTokenProviderCallback() : void {
    apiTokenProviderCallback = null;
}

export interface GroupCollection {
    groups: Group[],
    isComplete: boolean
}

export interface Group {
    azureGroupId: string,
    azureGroupName: string,
    azureGroupType: string
}

async function executeJsonMethodOnApi(authentication: any, method: string, relativeApiPath: string, jsonBody: any = null): Promise<any> {
    const response = await executeMethodOnApi(authentication, method, relativeApiPath, jsonBody);
    return await response.json();
}

async function executeBlobMethodOnApi(authentication: any, method: string, relativeApiPath: string, jsonBody: any = null): Promise<Blob> {
    const response = await executeMethodOnApi(authentication, method, relativeApiPath, jsonBody);
    return await response.blob();
}

async function executeMethodOnApi(authentication: any, method: string, relativeApiPath: string , bodyData: any = null): Promise<Response> {
    try {
        log.debug(`[executeCallOnBackend] Invoking '${method}' on '${relativeApiPath}'...`)

        const accessToken = await authentication.getApiTokenAsync();
    
        const url = `/api/${relativeApiPath}`;
        
        const requestHeaders: HeadersInit = new Headers();
        requestHeaders.set('Authorization', `Bearer ${accessToken.accessToken}`);

        const requestParameters: RequestInit = {
            method: method,
            headers: requestHeaders
        };
    
        // Add the body if one was provided.
        if (bodyData) {
            if (bodyData instanceof FormData)
            {
                requestParameters.body = bodyData;
            }
            else
            {
                requestHeaders.set('Content-Type', 'application/json');
                requestParameters.body = JSON.stringify(bodyData);
            }
        }
    
        const response = await fetch(url, requestParameters);
    
        if (!response.ok) {
            // Some non-success response received.
            throw new ServerErrorResponse(null, response.status, response.statusText, await response.text());
        }
    
        // A success response is received from the back-end.
        return response;
    }
    catch (error: any) {
        if (error instanceof ServerErrorResponse) {
            const responseBody = error.responseBody ? `\r\n${error.responseBody}` : "";
            log.error(`Server error response received while invoking '${method}' on '/${relativeApiPath}' endpoint. Received ${error.statusCode} '${error.statusText}'.${responseBody}`);
        } else {
            log.error(`Error occurred while invoking '${method}' on '/${relativeApiPath}': ${error.message}`);    
        }

        throw (error);
    }
}

export async function confirmLogin(authentication: any): Promise<any> {
    return await executeMethodOnApi(authentication, 'POST', 'tenant/loginconfirmation', null);
}

export async function getTenant(authentication: any): Promise<any> {
    return await executeJsonMethodOnApi(authentication, 'GET', 'tenant', null);
}

export async function tenantUpdateAuditTrailRetention(authentication: any,
                                            auditTrailRetentionDays: number): Promise<any> {
    const tenantDto = {
        auditTrailRetentionDays: auditTrailRetentionDays,
    }

    return await executeMethodOnApi(authentication, 'PATCH', 'tenant', tenantDto);
}

export async function getSubscriptions(authentication: any): Promise<any> {
    return await executeJsonMethodOnApi(authentication, 'GET', 'tenant/subscription', null);
}

export async function resolveMarketplaceToken(authentication: any, token: string): Promise<any> {
    var tokenDto = {
        Token: token,      
    }

    return await executeJsonMethodOnApi(authentication, 'POST', 'marketplace/resolvetoken', tokenDto);
}

export async function activateSubscription(authentication: any, subscriptionId: string): Promise<any> {

    return await executeMethodOnApi(authentication, 'POST', `marketplace/activatesubscription/${subscriptionId}`, null);
}

export async function updateMarketplaceSubscriptionQuantity(authentication: any, subscriptionId: string, quantity: number): Promise<any> {
    var quantityDto = {
        quantity: quantity           
    }

    return await executeMethodOnApi(authentication, 'POST', `marketplace/subscription/${subscriptionId}/quantity`, quantityDto);
}

export async function getDevices(authentication: any): Promise<any> {
    return await executeJsonMethodOnApi(authentication, 'GET', 'tenant/device', null);
}

export async function getDeviceEventTypes(authentication: any): Promise<any> {
    return await executeJsonMethodOnApi(authentication, 'GET', `tenant/device/eventtypes`, null);
}

export async function addDevice(authentication: any, referenceId: string, description: string, eventsToSuppress: string[], contactGroupId?: number, iqMessengerDeviceCode?: string, iqMessengerFallbackTimeoutSeconds?: number, iqMessengerConnectionId?: number): Promise<any> {
    var deviceDto = {
        referenceId: referenceId,
        deviceDescription: description,
        eventsToSuppress: eventsToSuppress,
        contactGroupId: contactGroupId,
        iqMessengerDeviceCode: iqMessengerDeviceCode,
        iqMessengerFallbackTimeoutSeconds: iqMessengerFallbackTimeoutSeconds,
        iqMessengerConnectionId: iqMessengerConnectionId,
    };

    return await executeMethodOnApi(authentication, 'POST', 'tenant/device', deviceDto);
}

export async function updateDevice(authentication: any, deviceId: number, referenceId: string, description: string, eventsToSuppress: string[], contactGroupId?: number, iqMessengerDeviceCode?: string, iqMessengerFallbackTimeoutSeconds?: number, iqMessengerConnectionId?: number): Promise<any> {
    var deviceDto = {
        referenceId: referenceId,
        deviceDescription: description,
        eventsToSuppress: eventsToSuppress,
        contactGroupId: contactGroupId,
        iqMessengerDeviceCode: iqMessengerDeviceCode,
        iqMessengerFallbackTimeoutSeconds: iqMessengerFallbackTimeoutSeconds,
        iqMessengerConnectionId: iqMessengerConnectionId,
    };

    return await executeMethodOnApi(authentication, 'PATCH', `tenant/device/${deviceId}`, deviceDto);
}

export async function deleteDevice(authentication: any, deviceId: number): Promise<any> {
    return await executeMethodOnApi(authentication, 'DELETE', `tenant/device/${deviceId}`, null);
}

export async function getMobotixConfiguration(authentication: any, deviceId: number): Promise<Blob> {
    return await executeBlobMethodOnApi(authentication, 'GET', `tenant/device/${deviceId}/mobotixconfiguration`, null);
}

export async function getIqMessengerConnections(authentication: any): Promise<any> {
    return await executeJsonMethodOnApi(authentication, 'GET', 'tenant/IqMessengerConnection', null);
}

export async function addIqMessengerConnection(authentication: any, name: string, hostname: string, clientId: string, clientSecret?: string): Promise<any> {
    var deviceDto = {
        name: name,
        clientId: clientId,
        clientSecret: clientSecret,
        hostname: hostname,
    };

    return await executeMethodOnApi(authentication, 'POST', 'tenant/IqMessengerConnection', deviceDto);
}

export async function updateIqMessengerConnection(authentication: any, connectionId: number, name: string, hostname: string, clientId: string, clientSecret?: string): Promise<any> {
    var deviceDto = {
        name: name,
        clientId: clientId,
        clientSecret: clientSecret,
        hostname: hostname,
    };

    return await executeMethodOnApi(authentication, 'PATCH', `tenant/IqMessengerConnection/${connectionId}`, deviceDto);
}

export async function deleteIqMessengerConnection(authentication: any, connectionId: number): Promise<any> {
    return await executeMethodOnApi(authentication, 'DELETE', `tenant/IqMessengerConnection/${connectionId}`, null);
}

export async function getContacts(authentication: any): Promise<any> {
    return await executeJsonMethodOnApi(authentication, 'GET', 'tenant/contact', null);
}

export async function addContact(authentication: any, name: string, mobilePhoneNumber: string): Promise<any> {
    var contactDto = {
        name: name,
        mobilePhoneNumber: mobilePhoneNumber,
    };

    return await executeMethodOnApi(authentication, 'POST', 'tenant/contact', contactDto);
}

export async function updateContact(authentication: any, contactId: number, name: string, mobilePhoneNumber: string): Promise<any> {
    var contactDto = {
        name: name,
        mobilePhoneNumber: mobilePhoneNumber,
    };

    return await executeMethodOnApi(authentication, 'PATCH', `tenant/contact/${contactId}`, contactDto);
}

export async function deleteContact(authentication: any, contactId: number): Promise<any> {
    return await executeMethodOnApi(authentication, 'DELETE', `tenant/contact/${contactId}`, null);
}

export async function getContactGroups(authentication: any): Promise<any> {
    return await executeJsonMethodOnApi(authentication, 'GET', 'tenant/contactgroup', null);
}

export async function addContactGroup(authentication: any, name: string, description: string): Promise<any> {
    var contactGroupDto = {
        name: name,
        description: description,
    };

    return await executeMethodOnApi(authentication, 'POST', 'tenant/contactgroup', contactGroupDto);
}

export async function updateContactGroup(authentication: any, contactGroupId: number, name: string, description: string): Promise<any> {
    var contactGroupDto = {
        name: name,
        description: description,
    };

    return await executeMethodOnApi(authentication, 'PATCH', `tenant/contactgroup/${contactGroupId}`, contactGroupDto);
}

export async function deleteContactGroup(authentication: any, contactGroupId: number): Promise<any> {
    return await executeMethodOnApi(authentication, 'DELETE', `tenant/contactgroup/${contactGroupId}`, null);
}

export async function addContactGroupEntry(authentication: any, contactGroupId: number, contactId: number): Promise<any> {
    var contactAssignDto = {
        contactId: contactId,
    };

    return await executeMethodOnApi(authentication, 'POST',  `tenant/contactgroup/${contactGroupId}/Entries`, contactAssignDto);
}

export async function deleteContactGroupEntry(authentication: any, contactGroupId: number, contactId: number): Promise<any> {
    return await executeMethodOnApi(authentication, 'DELETE', `tenant/contactgroup/${contactGroupId}/Entries/${contactId}`, null);
}

export async function getPortalAccess(authentication: any): Promise<Array<Group>> {
    return await executeJsonMethodOnApi(authentication, 'GET', 'tenant/portalaccess', null);
}

export async function addPortalAccess(authentication: any, azureGroupId: string): Promise<any> {
    var portalAccessDto = {
        azureGroupId: azureGroupId
    }

    return await executeMethodOnApi(authentication, 'POST', `tenant/portalaccess`, portalAccessDto);
}

export async function deletePortalAccess(authentication: any, azureGroupId: string): Promise<any> {
    return await executeMethodOnApi(authentication, 'DELETE', `tenant/portalaccess/${azureGroupId}`, null);
}

export async function getAvailableGroups(authentication: any): Promise<GroupCollection> {
    return await executeJsonMethodOnApi(authentication, 'GET', 'tenant/groups', null);
}

export async function getAuditTrail(authentication: any, pageSize: number, pageNumber: number): Promise<any> {
    return await executeJsonMethodOnApi(authentication, 'GET', `tenant/audittrail?pageSize=${pageSize}&pageNumber=${pageNumber}`, null);
}

export async function getAuditTrailForUser(authentication: any, userId: string, pageSize: number, pageNumber: number): Promise<any> {
    return await executeJsonMethodOnApi(authentication, 'GET', `tenant/audittrail/${userId}?pageSize=${pageSize}&pageNumber=${pageNumber}`, null);
}

export async function getAuditTrailUsers(authentication: any): Promise<any> {
    return await executeJsonMethodOnApi(authentication, 'GET', 'tenant/audittrail/users', null);
}
